import { KUBE_SNAP_OVERLAP_MM } from '@constants'
import { useAppStore } from '@store'
import type { AnyID, Item, LinkedItem } from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isLinkedSon from '@utils/sceneObjects/assertions/isLinkedSon'
import { cloneLinkedItemTexturesForInheritance } from '@utils/sceneObjects/getExistingTexturesFromScene'
import {
  hydrateKubeMeshLocalMins,
  resolveKubeCushionDrawPose,
} from '@utils/modularSnap/kubeCushion'
import { isKubeBase, isKubeCushion, isKubeModule } from '@utils/sceneObjects/items/rinconeroUtils'
import { Matrix4, Quaternion, Vector3 } from 'three'

const isArmCatalog = (catalogID: unknown) => /^BR\d+(-\d+)?$/i.test(String(catalogID || ''))

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const cloneMorphs = (morphs: Item['morphs'] | undefined) =>
  morphs?.map((morph) => ({
    ...morph,
    default: morph.default,
    _default: morph._default ?? morph.default,
  })) ?? []

const unitWidth = (unit: { width?: number }) => {
  const w = Number(unit?.width || 0)
  return Number.isFinite(w) && w > 0 ? w : 0
}

const isDuplicableCushion = (item: any) =>
  Boolean(
    item &&
    isLinkedItem(item) &&
    item.universal === false &&
    item.visible !== false &&
    isKubeCushion(item.catalogID),
  )

const isBaseLinkedUnit = (item: any) =>
  Boolean(
    item &&
    isLinkedItem(item) &&
    item.universal === false &&
    item.visible !== false &&
    !isArmCatalog(item.catalogID) &&
    !isKubeCushion(item.catalogID),
  )

/** Resuelve la unidad o cojín seleccionable para duplicar / info de selección. */
export function resolveSelectedLinkedUnit(
  items: Map<AnyID, any>,
  selected: Iterable<string | AnyID>,
  currentObjectID?: AnyID | string | '',
): LinkedItem | null {
  const candidateIds = [
    ...(currentObjectID ? [String(currentObjectID)] : []),
    ...Array.from(selected, String),
  ]

  for (const id of candidateIds) {
    const item = items.get(id as AnyID)
    if (!item) continue

    // Cojín libre: se duplica él mismo (no el padre).
    if (isDuplicableCushion(item)) return item

    if (isBaseLinkedUnit(item)) return item

    if (isLinkedSon(item) && item.firstParentID) {
      // Si el hijo es cojín kube, preferir el propio cojín cuando sea linkedItem;
      // los linkedSon de asiento/cojín auto no se duplican aquí → padre.
      const parent = items.get(item.firstParentID as AnyID)
      if (isBaseLinkedUnit(parent)) return parent
    }
  }

  return null
}

function findNearestKubeBase(items: Map<AnyID, any>, source: LinkedItem): LinkedItem | null {
  if (!source?.matrix) return null
  const sourcePos = new Vector3()
  source.matrix.decompose(sourcePos, new Quaternion(), new Vector3())

  let best: LinkedItem | null = null
  let bestDist = Infinity
  items.forEach((candidate) => {
    if (!isLinkedItem(candidate) || !candidate.matrix) return
    if (!isKubeBase(candidate.catalogID)) return
    const pos = new Vector3()
    candidate.matrix.decompose(pos, new Quaternion(), new Vector3())
    const dist = pos.distanceToSquared(sourcePos)
    if (dist < bestDist) {
      bestDist = dist
      best = candidate
    }
  })
  return best
}

function resolveCushionDuplicatePlacement(
  items: Map<AnyID, any>,
  source: LinkedItem,
): { matrix: Matrix4; anchorId: AnyID | null } | null {
  if (!source?.matrix) return null

  hydrateKubeMeshLocalMins(items as any, useAppStore.getState().sceneGLB as Map<any, any>)

  const base = findNearestKubeBase(items, source)
  const pose = resolveKubeCushionDrawPose(
    `dup-${String(source.id)}` as AnyID,
    source,
    items as any,
    base,
  )

  // Si no había otros cojines, resolveKubeCushionDrawPose puede devolver la pose
  // sobre la base (misma zona). Empujar al lado del cojín origen.
  const sourcePos = new Vector3()
  const sourceQuat = new Quaternion()
  source.matrix.decompose(sourcePos, sourceQuat, new Vector3())
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(sourceQuat)
  const gap = Math.max(unitWidth(source), 1) + 8

  let position = pose.position.clone()
  let quaternion = pose.quaternion.clone()

  const tooClose =
    Math.hypot(position.x - sourcePos.x, position.z - sourcePos.z) <
    Math.max(unitWidth(source) * 0.4, 40)

  if (tooClose) {
    position = sourcePos.clone().addScaledVector(xAxis, gap)
    position.y = sourcePos.y
    quaternion = sourceQuat.clone()
  }

  const matrix = new Matrix4().compose(position, quaternion, new Vector3(1, 1, 1))
  return { matrix, anchorId: base ? (String(base.id) as AnyID) : null }
}

type DuplicatePlacement = {
  anchor: LinkedItem
  side: 'left' | 'right'
  matrix: Matrix4
}

/**
 * Busca el extremo libre de la fila (preferencia derecha) y compone la matriz
 * al lado del ancla, usando el ancho real de colisión (item.width), no morphs.
 */
function resolveDuplicatePlacement(
  items: Map<AnyID, any>,
  source: LinkedItem,
): DuplicatePlacement | null {
  if (!source?.matrix) return null

  const sourcePos = new Vector3()
  const sourceQuat = new Quaternion()
  source.matrix.decompose(sourcePos, sourceQuat, new Vector3())
  const sourceX = new Vector3(1, 0, 0).applyQuaternion(sourceQuat)
  const sourceZ = new Vector3(0, 0, 1).applyQuaternion(sourceQuat)

  const SIDE_TOLERANCE_MM = 80
  const DEPTH_ALIGNMENT_TOLERANCE_MM = 140
  const HEIGHT_TOLERANCE_MM = 140

  type RowMember = { item: LinkedItem; leftEdge: number; rightEdge: number }
  const row: RowMember[] = []

  items.forEach((candidate) => {
    if (!isBaseLinkedUnit(candidate) || !candidate.matrix) return
    const pos = new Vector3()
    candidate.matrix.decompose(pos, new Quaternion(), new Vector3())
    const delta = pos.clone().sub(sourcePos)
    const along = sourceX.dot(new Vector3(delta.x, 0, delta.z))
    const depth = Math.abs(sourceZ.dot(new Vector3(delta.x, 0, delta.z)))
    const heightDelta = Math.abs(delta.y)
    if (depth > DEPTH_ALIGNMENT_TOLERANCE_MM || heightDelta > HEIGHT_TOLERANCE_MM) return

    const w = unitWidth(candidate)
    // Origen en esquina izquierda local → arista izq = along, arista der = along + w
    row.push({ item: candidate, leftEdge: along, rightEdge: along + w })
  })

  if (!row.length) {
    row.push({
      item: source,
      leftEdge: 0,
      rightEdge: unitWidth(source),
    })
  }

  let leftEnd = row[0]
  let rightEnd = row[0]
  for (const member of row) {
    if (member.leftEdge < leftEnd.leftEdge) leftEnd = member
    if (member.rightEdge > rightEnd.rightEdge) rightEnd = member
  }

  const sideOccupied = (anchor: LinkedItem, side: 'left' | 'right') => {
    const snapped = (anchor as any).snappedSides as
      Partial<Record<'left' | 'right', string>> | undefined
    if (side === 'right' && snapped?.right) return true
    if (side === 'left' && snapped?.left) return true

    const aPos = new Vector3()
    const aQuat = new Quaternion()
    anchor.matrix.decompose(aPos, aQuat, new Vector3())
    const aX = new Vector3(1, 0, 0).applyQuaternion(aQuat)
    const aZ = new Vector3(0, 0, 1).applyQuaternion(aQuat)
    const aWidth = unitWidth(anchor)

    for (const candidate of items.values()) {
      if (!isBaseLinkedUnit(candidate) || !candidate.matrix) continue
      if (String(candidate.id) === String(anchor.id)) continue
      const cPos = new Vector3()
      candidate.matrix.decompose(cPos, new Quaternion(), new Vector3())
      const delta = new Vector3(cPos.x - aPos.x, cPos.y - aPos.y, cPos.z - aPos.z)
      const sideProjection = aX.dot(new Vector3(delta.x, 0, delta.z))
      const depthProjection = Math.abs(aZ.dot(new Vector3(delta.x, 0, delta.z)))
      if (depthProjection > DEPTH_ALIGNMENT_TOLERANCE_MM) continue
      if (Math.abs(delta.y) > HEIGHT_TOLERANCE_MM) continue

      if (side === 'right') {
        if (Math.abs(sideProjection - aWidth) <= SIDE_TOLERANCE_MM) return true
      } else {
        if (Math.abs(sideProjection + unitWidth(candidate)) <= SIDE_TOLERANCE_MM) return true
      }
    }
    return false
  }

  const preferRight = !sideOccupied(rightEnd.item, 'right')
  const preferLeft = !sideOccupied(leftEnd.item, 'left')

  let anchor = rightEnd.item
  let side: 'left' | 'right' = 'right'
  if (preferRight) {
    anchor = rightEnd.item
    side = 'right'
  } else if (preferLeft) {
    anchor = leftEnd.item
    side = 'left'
  } else {
    // Ambos extremos ocupados: no hay sitio libre en la fila.
    return null
  }

  const drawWidth = unitWidth(source)
  if (!(drawWidth > 0) || !(unitWidth(anchor) > 0)) return null

  const aPos = new Vector3()
  const aQuat = new Quaternion()
  anchor.matrix.decompose(aPos, aQuat, new Vector3())
  const aX = new Vector3(1, 0, 0).applyQuaternion(aQuat)

  const kubeOverlap =
    isKubeModule(source.catalogID) &&
    !isKubeCushion(source.catalogID) &&
    isKubeModule(anchor.catalogID) &&
    !isKubeCushion(anchor.catalogID)
      ? KUBE_SNAP_OVERLAP_MM
      : 0

  const offset =
    side === 'right'
      ? Math.max(0, unitWidth(anchor) - kubeOverlap)
      : -Math.max(0, drawWidth - kubeOverlap)

  const pos = aPos.clone().add(aX.multiplyScalar(offset))
  const matrix = new Matrix4().compose(pos, aQuat, new Vector3(1, 1, 1))

  return { anchor, side, matrix }
}

const buildChildFinishPatch = (child: any, duplicatedParent: LinkedItem, newParentId: AnyID) => ({
  textures: Array.isArray(child.textures) ? [...child.textures] : child.textures,
  zones: child.zones ? { ...child.zones } : child.zones,
  idUnidades: child.idUnidades,
  x: child.x,
  y: child.y,
  z: child.z,
  width: duplicatedParent.width,
  height: duplicatedParent.height,
  thickness: duplicatedParent.thickness,
  morphs: cloneMorphs(child.morphs),
  glbSize: child.glbSize,
  referenciaBlender: child.referenciaBlender,
  catalogID: child.catalogID,
  linkedID: child.linkedID,
  description: child.description,
  visualOrder: child.visualOrder,
  quantity: child.quantity,
  matrix: child.matrix,
  glbPath: child.glbPath,
  universal: child.universal,
  depth: child.depth,
  parentOption: child.parentOption,
  option: child.option,
  handPosition: child.handPosition,
  hand: child.hand,
  typeOfSon: child.typeOfSon,
  isUnlinkable: child.isUnlinkable,
  firstParentID: newParentId,
  idElementosEnlazados: child.idElementosEnlazados,
  code: child.code,
  long: child.long,
  referencia: child.referencia,
  increment: child.increment,
  paginaPdf: child.paginaPdf,
  tipoNormalizado: child.tipoNormalizado,
  tipoUnidNorm: child.tipoUnidNorm,
  url: child.url,
})

/**
 * Duplica la unidad o cojín seleccionado y lo coloca sin pisar otras piezas.
 */
export async function duplicateSelectedLinkedUnitAtRightEnd(): Promise<AnyID | null> {
  const store = useAppStore.getState()
  const selected = store.layers[store.currentLayer]?.selected
  if (!selected?.size && !store.currentObjectID) return null

  const source = resolveSelectedLinkedUnit(store.items, selected || [], store.currentObjectID)
  if (!source?.matrix || (source as any).isBlocked) return null

  const isCushion = isKubeCushion(source.catalogID)
  let relativeMatrix: Matrix4
  let anchorId: AnyID | null = null
  let snapSide: 'left' | 'right' | null = null

  if (isCushion) {
    const cushionPlacement = resolveCushionDuplicatePlacement(
      store.items as Map<AnyID, any>,
      source,
    )
    if (!cushionPlacement) {
      console.warn('No se pudo calcular una posición libre para duplicar el cojín.')
      return null
    }
    relativeMatrix = cushionPlacement.matrix
    anchorId = cushionPlacement.anchorId
  } else {
    const placement = resolveDuplicatePlacement(store.items as Map<AnyID, any>, source)
    if (!placement) {
      console.warn('No hay un lado libre para duplicar la unidad seleccionada.')
      return null
    }
    relativeMatrix = placement.matrix
    anchorId = String(placement.anchor.id) as AnyID
    snapSide = placement.side
  }

  const sourceChildrenIds =
    source.childrenOrder?.map((el) => el.split('!')[1] as AnyID).filter(Boolean) ?? []

  store.unselectAll()

  const newId = await store.drawItem(
    String(source.id),
    String(source.catalogID),
    source.image,
    true,
    relativeMatrix,
    undefined,
    undefined,
    false,
    {
      preserveWorldTransform: true,
      suppressCameraFit: true,
      preDrawSelectedAnchorId: anchorId || '',
      inheritTexturesFrom: cloneLinkedItemTexturesForInheritance(source),
    },
  )

  if (!newId) return null

  // Solo módulos en fila: snap lateral. Los cojines ya vienen colocados al lado.
  if (!isCushion && anchorId && snapSide) {
    try {
      store.collideItems(newId, anchorId, { forcedSide: snapSide })
    } catch (error) {
      console.warn('No se pudo aplicar snap lateral tras duplicar:', error)
    }
  } else if (isCushion) {
    // Reafirmar pose libre del cojín (por si el post-proceso movió algo).
    const pos = toVector3()
    const quat = toQuaternion()
    relativeMatrix.decompose(pos, quat, toVector3())
    store.editItem(newId, {
      x: pos.x,
      y: pos.y,
      z: pos.z,
      matrix: relativeMatrix.clone(),
    } as any)
  }

  await wait(120)

  const liveAfterSnap = useAppStore.getState().items.get(newId)
  if (!isLinkedItem(liveAfterSnap)) return newId

  // Conservar la pose ya snapeada; solo copiar acabados / morfologías.
  store.editItem(newId, {
    textures: Array.isArray(source.textures) ? [...source.textures] : source.textures,
    zones: source.zones ? { ...source.zones } : source.zones,
    morphs: cloneMorphs(source.morphs),
    width: source.width,
    height: source.height,
    thickness: source.thickness,
    visible: true,
    seatStitchType: (source as any).seatStitchType,
    seatHeightCm: (source as any).seatHeightCm,
  } as any)

  await wait(80)

  const liveDuplicated = useAppStore.getState().items.get(newId)
  if (!isLinkedItem(liveDuplicated)) {
    store.select(newId, false)
    return newId
  }

  const duplicatedChildrenIds =
    liveDuplicated.childrenOrder?.map((el) => el.split('!')[1] as AnyID).filter(Boolean) ?? []

  const sourceChildren = sourceChildrenIds
    .map((id) => useAppStore.getState().items.get(id))
    .filter(isLinkedSon) as any[]

  const usedSourceIndexes = new Set<number>()
  duplicatedChildrenIds.forEach((dupChildId, index) => {
    const dupChild = useAppStore.getState().items.get(dupChildId)
    if (!isLinkedSon(dupChild)) return

    let sourceChild = sourceChildren.find(
      (child, i) =>
        !usedSourceIndexes.has(i) &&
        String(child.catalogID || '').toLowerCase() ===
          String(dupChild.catalogID || '').toLowerCase(),
    )
    if (sourceChild) {
      const matchedIndex = sourceChildren.indexOf(sourceChild)
      usedSourceIndexes.add(matchedIndex)
    } else {
      sourceChild = sourceChildren[index]
      if (sourceChild) usedSourceIndexes.add(index)
    }
    if (!sourceChild) return

    store.editItem(dupChildId, buildChildFinishPatch(sourceChild, liveDuplicated, newId) as any)
  })

  await wait(60)
  useAppStore.getState().setOBBScene?.()
  store.select(newId, false)

  return newId
}
